// src/components/FileUploadArea.jsx
import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { fileSystemUrl } from '../../../constants/Connections'
import TemplateSection from './TemplateSection'

function FileUploadArea({ onFileSelected, uploadedFile, onFileRemoved, isProcessing, processingProgress }) {
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef(null)
    const dropAreaRef = useRef(null)
    const { t, locale } = useTranslation()

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        const fileExtension = file.name.split('.').pop().toLowerCase()
        if (['xlsx', 'xls'].includes(fileExtension)) {
            onFileSelected(file)
        } else {
            alert('Please upload only Excel files (.xlsx, .xls)')
        }
    }

    useEffect(() => {
        console.log('Processing progress updated:', processingProgress)
    }, [processingProgress])

    const handleDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!isDragging) setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()

        if (dropAreaRef.current && !dropAreaRef.current.contains(e.relatedTarget)) {
            setIsDragging(false)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0]
            const fileExtension = file.name.split('.').pop().toLowerCase()

            if (['xlsx', 'xls'].includes(fileExtension)) {
                onFileSelected(file)
            } else {
                alert('Please upload only Excel files (.xlsx, .xls)')
            }
        }
    }

    const handleButtonClick = () => {
        fileInputRef.current.click()
    }

    const handleRemoveFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
        onFileRemoved()
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const formatDate = (date) => {
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date)
    }

    if (isProcessing) {
        return (
            <div className="w-full">
                {/* Processing Header */}

                <div className="flex flex-row items-center gap-4 mb-8">
                    <div className="flex items-center justify-center w-[50px] h-[50px] bg-[#DEDCFB] rounded-full ">
                        <svg className="w-8 h-8 text-deepPurple animate-spin-slow-clockwise" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                    </div>
                    <div className="space-y-3">
                        <h3 className="text-[31px] leading-[39px] tracking-tight  font-bold text-darkPurple mb-3">{t('Processing Your File')}</h3>
                        <p className="text-darkPurple text-[14px] leading-[23px]  font-normal">{t('Analyzing Excel data and preparing for import...')}</p>
                    </div>
                </div>

                {/* File Info Card with Progress */}
                <div className="bg-[#F8F8FB] rounded-[16px] pt-11 pb-8 px-10">
                    <div className="bg-[#ECEBF8] rounded-[16px] border-1 border-[#BEBECE] pt-6 pr-5 pb-5 pl-6 ">
                        <div className="flex items-center space-x-4 ">
                            {/* File Icon */}

                            <div className="w-[65px] bg-white h-[65px] rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-deepPurple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                            </div>

                            {/* File Details */}
                            <div className="flex-1 flex flex-col gap-3 ">
                                <h4 className="text-[20px] leading-5 font-bold text-darkPurple truncate ">{uploadedFile?.name ?? '-'}</h4>
                                <div className="flex items-center space-x-4 text-deepPurple text-[16px] leading-[18px]  font-medium">
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 3v12a2 2 0 002 2h8a2 2 0 002-2V7m-9 0h10M9 7h6"
                                            />
                                        </svg>
                                        {formatFileSize(uploadedFile?.size)}
                                    </span>
                                </div>
                            </div>

                            {/* Processing Badge */}
                            <div className="flex-shrink-0">
                                <span className="inline-flex items-center px-3 h-[30px] rounded-full text-[16px] leading-[18px] font-medium bg-[#DEDCFB] text-deepPurple">
                                    <svg className="w-4 h-4 mr-1 animate-spin-slow-clockwise" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                        />
                                    </svg>
                                    {t('Processing')}...
                                </span>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        {/* <div className="space-y-[10px] mt-[35px]">
                            <div className="w-full bg-[#DEDCFB] rounded-full h-[10px] overflow-hidden">
                                <div className="bg-[#5E639B] h-[10px] rounded-full transition-all duration-200 ease-out" style={{ width: `${processingProgress}%` }}>
                                    <div className="h-full bg-[#5E639B] bg-opacity-30 rounded-full "></div>
                                </div>
                            </div>
                            <div className="flex  justify-between ">
                                <span className="text-[14px] leading-5 font-medium text-darkPurple">{t('Processing progress')}</span>
                                <span className="text-[14px] leading-5 font-medium text-darkPurple">{Math.round(processingProgress)}%</span>
                            </div>
                        </div> */}

                        {/* Processing Steps */}
                    </div>
                </div>

                {/* Processing Tips */}
                <div className="bg-[#F8F8FB] rounded-[16px] p-5  mt-4">
                    <div className="flex items-start space-x-4">
                        <div className="flex-1">
                            <div className="flex flex-row items-center gap-3">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                                        fill="#42405f"
                                    />
                                </svg>
                                <h4 className="text-[24px] font-bold leading-6 text-darkPurple tracking-tight ">{t("What's happening now?")}</h4>
                            </div>
                            <div className="mt-6">
                                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-3 ml-[10px]">
                                    <li>• {t('Reading and parsing your Excel file structure')}</li>
                                    <li>• {t('Identifying student and staff data sheets')}</li>
                                    <li>• {t('Validating data format and required fields')}</li>
                                    <li>• {t('Preparing data for import configuration')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // If file is uploaded, show the success state
    if (uploadedFile && !isProcessing) {
        return (
            <div className="w-full">
                {/* Success Header */}

                <div className="flex flex-row items-center gap-4 mb-8">
                    <div className="flex items-center justify-center w-[50px] h-[50px] bg-[#D7EFDF] rounded-full ">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.55156 17.9996L3.85156 12.2996L5.27656 10.8746L9.55156 15.1496L18.7266 5.97461L20.1516 7.39961L9.55156 17.9996Z" fill="#2F8946" />
                        </svg>
                    </div>
                    <div className="space-y-3">
                        <h3 className="text-[31px] leading-[39px] tracking-tight  font-bold text-darkPurple mb-3">{t('File Uploaded Successfully!')}</h3>
                        <p className="text-darkPurple text-[14px] leading-[23px]  font-normal">{t('Your Excel file has been processed and is ready for import')}</p>
                    </div>
                </div>

                {/* File Info Card */}
                <div className="bg-[#F8F8FB] rounded-[16px] py-11 px-10">
                    <div className="bg-[#D7EFDF] rounded-xl border border-[#BEBECE] p-6">
                        <div className="flex items-center space-x-4">
                            {/* File Icon */}
                            <div className="w-[65px] bg-white h-[65px] rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                            </div>

                            {/* File Details */}
                            <div className="flex-1 flex flex-col gap-3 ">
                                <h4 className="text-[20px] leading-5 font-bold text-darkPurple truncate ">{uploadedFile.name}</h4>
                                <div className="flex items-center space-x-4 text-deepPurple text-[14px] leading-[18px]  font-medium">
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 3v12a2 2 0 002 2h8a2 2 0 002-2V7m-9 0h10M9 7h6"
                                            />
                                        </svg>
                                        {formatFileSize(uploadedFile.size)}
                                    </span>
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {formatDate(new Date(uploadedFile.lastModified))}
                                    </span>
                                </div>
                            </div>

                            {/* Success Badge */}
                            <div className="-mt-10">
                                <span className="inline-flex items-center px-3  h-[30px] rounded-full text-[16px] leading-[18px] font-medium bg-[#2F8946] text-white">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('Ready to process')}
                                </span>
                            </div>
                        </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 mt-[13px]">
                        <button
                            type="button"
                            onClick={handleRemoveFile}
                            className="inline-flex items-center px-4 h-[50px]  border border-[#FC4C59] hover:bg-[#FC4C59] rounded-full text-[#FC4C59] hover:text-white text-[16px] leading-5 font-semibold transition-all duration-300 "
                        >
                            {t('Remove File')}
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M5 21.5V6.5H4V4.5H9V3.5H15V4.5H20V6.5H19V21.5H5ZM7 19.5H17V6.5H7V19.5ZM9 17.5H11V8.5H9V17.5ZM13 17.5H15V8.5H13V17.5Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={handleButtonClick}
                            className="inline-flex items-center px-4 h-[50px]  border border-[#2F8946] hover:bg-[#2F8946] rounded-full text-[#2F8946] hover:text-white text-[16px] leading-5 font-semibold transition-all duration-300 "
                        >
                            {t('Upload Another')}
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M13.3232 8.9359L9.49421 12.6899C9.13021 13.0499 8.93021 13.5419 8.93821 14.0589C8.94912 14.5819 9.16451 15.0798 9.53821 15.4459C9.91321 15.8169 10.4182 16.0299 10.9412 16.0389C11.1972 16.0443 11.4518 15.9984 11.6898 15.9039C11.9278 15.8095 12.1445 15.6684 12.3272 15.4889L16.1552 11.7349C16.5163 11.3776 16.8011 10.9507 16.9923 10.4801C17.1834 10.0094 17.277 9.5048 17.2672 8.9969C17.2462 7.95082 16.8161 6.95462 16.0692 6.2219C15.3202 5.48075 14.3147 5.0564 13.2612 5.0369C12.7497 5.02669 12.2413 5.11853 11.7657 5.30706C11.2901 5.49559 10.8568 5.77701 10.4912 6.1349L6.66021 9.8899C6.11891 10.4261 5.69207 11.0665 5.40553 11.7724C5.11899 12.4784 4.97872 13.2352 4.99321 13.9969C5.02541 15.5656 5.67085 17.0594 6.79121 18.1579C7.91406 19.2693 9.42159 19.906 11.0012 19.9359C11.7688 19.9519 12.5318 19.8145 13.2456 19.5319C13.9594 19.2493 14.6097 18.827 15.1582 18.2899L18.9872 14.5339"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>{' '}
                </div>

                <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} />
            </div>
        )
    }

    // Default upload area when no file is uploaded
    return (
        <div className="w-full">
            {/* Upload Header */}
            <div className="flex flex-row items-center gap-4 mb-8">
                <div className="flex items-center justify-center w-[50px] h-[50px] bg-[#DEDCFB] rounded-full ">
                    <svg className={`w-6 h-6 transition-colors duration-300 text-deepPurple `} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                    </svg>
                </div>
                <div className="space-y-3">
                    <h3 className="text-[31px] leading-[39px] tracking-tight  font-bold text-darkPurple mb-3">{t('Import User Data')}</h3>
                    <p className="text-darkPurple text-[14px] leading-[23px]  font-normal">{t('Select an Excel file containing user information for bulk import')}</p>
                </div>
            </div>

            {/* Template Download Box */}
            <TemplateSection />
            {/* Main Upload Area */}
            <div className="bg-[#F8F8FB] rounded-[16px] py-11 px-10">
                <div
                    ref={dropAreaRef}
                    className={`relative rounded-[16px]  border transition-all duration-300 ${
                        isDragging ? 'border-deepPurple bg-[#5e639b66] scale-[1.02]' : 'border-[#BEBECE] bg-[#F8F8FB] '
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className="px-6 py-[44px] text-center">
                        {/* Upload Icon */}
                        <div className={`inline-flex items-center justify-center w-[65px] bg-[#DEDCFB] h-[65px] rounded-full `}>
                            <svg className={`w-10 h-10 transition-colors duration-300 text-deepPurple`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                            </svg>
                        </div>

                        {/* Upload Text */}
                        <div className="space-y-5 mt-6">
                            <h4 className={`text-[24px] leading-5 font-bold transition-colors duration-300 ${isDragging ? 'text-deepPurple' : 'text-darkPurple'}`}>
                                {isDragging ? t('Drop your file here') : t('Drag and drop your Excel file')}
                            </h4>
                            <p className="text-deepPurple text-[16px] leading-[18px]  font-medium">{t('or click the button below to browse your files')}</p>
                        </div>

                        {/* Upload Button */}
                        <button
                            type="button"
                            onClick={handleButtonClick}
                            className={`mt-7 h-[50px] inline-flex items-center px-8  rounded-full font-semibold text-highlightColor transition-all duration-300 transform  ${
                                isDragging ? 'bg-[#5e639b66] text-highlightColor ' : 'bg-deepPurple text-highlightColor hover:bg-highlightColor hover:text-deepPurple '
                            }`}
                        >
                            {t('Browse Files')}
                            <svg className="ml-[10px]" width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M13.3232 8.9359L9.49421 12.6899C9.13021 13.0499 8.93021 13.5419 8.93821 14.0589C8.94912 14.5819 9.16451 15.0798 9.53821 15.4459C9.91321 15.8169 10.4182 16.0299 10.9412 16.0389C11.1972 16.0443 11.4518 15.9984 11.6898 15.9039C11.9278 15.8095 12.1445 15.6684 12.3272 15.4889L16.1552 11.7349C16.5163 11.3776 16.8011 10.9507 16.9923 10.4801C17.1834 10.0094 17.277 9.5048 17.2672 8.9969C17.2462 7.95082 16.8161 6.95462 16.0692 6.2219C15.3202 5.48075 14.3147 5.0564 13.2612 5.0369C12.7497 5.02669 12.2413 5.11853 11.7657 5.30706C11.2901 5.49559 10.8568 5.77701 10.4912 6.1349L6.66021 9.8899C6.11891 10.4261 5.69207 11.0665 5.40553 11.7724C5.11899 12.4784 4.97872 13.2352 4.99321 13.9969C5.02541 15.5656 5.67085 17.0594 6.79121 18.1579C7.91406 19.2693 9.42159 19.906 11.0012 19.9359C11.7688 19.9519 12.5318 19.8145 13.2456 19.5319C13.9594 19.2493 14.6097 18.827 15.1582 18.2899L18.9872 14.5339"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>

                        <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} />
                    </div>

                    {/* Drag Overlay */}
                    {isDragging && (
                        <div className="absolute inset-0 rounded-xl bg-upstrive-purple bg-opacity-100 flex items-center justify-center animate-fade-in">
                            <div className="text-center">
                                <div className={`inline-flex items-center justify-center w-[65px] bg-[#DEDCFB] h-[65px] rounded-full `}>
                                    <svg className={`w-10 h-10 transition-colors duration-300 text-deepPurple`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                </div>
                                <p className="text-xl font-semibold text-white">{t('Release to upload')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* File Requirements */}
            <div className="bg-[#F8F8FB] rounded-[16px] p-5  mt-[42px]">
                <div className="flex items-start space-x-4">
                    <div className="flex-1">
                        <div className="flex flex-row items-center gap-3">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                                    fill="#42405f"
                                />
                            </svg>
                            <h4 className="text-[24px] font-bold leading-6 text-darkPurple tracking-tight ">{t('File Requirements')}</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                            <div className="space-y-3">
                                <p className="font-bold text-[16px] leading-5 text-darkPurple">{t('Supported Formats')}:</p>

                                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-3 ml-[10px]">
                                    <li>• {t('Excel 2007+ (.xlsx)')}</li>
                                    <li>• {t('Excel 97-2003 (.xls)')}</li>
                                </ul>
                            </div>
                            <div className="space-y-3">
                                <p className="font-bold text-[16px] leading-5 text-darkPurple">{t('File Structure')}:</p>

                                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-3 ml-[10px]">
                                    <li>• {t('Must include headers')}</li>
                                    <li>• {t('Separate sheets for students/staff')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FileUploadArea
